import ms from 'ms'
import React, { useRef, useState } from 'react'
import { useDrop } from 'react-aria'
import { twJoin } from 'tailwind-merge'

import useTypedTranslation from '@hooks/useTypedTranslation'

interface FileUploadProps {
  accept?: string
  onChange: (file: File | null) => void
  children?: React.ReactNode
  className?: string
}

function FileUpload({ accept, onChange, children, className }: FileUploadProps) {
  const { t } = useTypedTranslation()
  const inputRef = useRef<HTMLInputElement | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [isUnaccepted, setIsUnaccepted] = useState<boolean>(false)

  function triggerFileSelect() {
    inputRef.current?.click()
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const selected = e.target.files?.[0] ?? null
    onChange(selected)
    if (inputRef.current) inputRef.current.value = ''
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      triggerFileSelect()
    }
  }

  // Helper to check if a file matches the accept patterns (subset of input accept behavior)
  function matchesAccept(file: File): boolean {
    if (!accept) return true
    const patterns = accept.split(',').map(s => s.trim()).filter(Boolean)
    if (!patterns.length) return true
    const fileName = file.name.toLowerCase()
    const fileType = file.type
    return patterns.some(pattern => {
      if (pattern.startsWith('.')) return fileName.endsWith(pattern.toLowerCase())
      if (pattern.endsWith('/*')) return fileType.startsWith(pattern.slice(0, -1))
      return fileType === pattern
    })
  }

  const { dropProps, isDropTarget } = useDrop({
    ref: containerRef,
    async onDrop(e) {
      for (const item of e.items) {
        if (item.kind === 'file') {
          try {
            const file = await item.getFile()
            if (matchesAccept(file)) {
              onChange(file)
              break
            } else {
              setIsUnaccepted(true)
              setTimeout(() => setIsUnaccepted(false), ms('5s'))
            }
          } catch {
            // ignore
          }
        }
      }
    },
  })

  const cn = twJoin(
    className,
    'cursor-pointer rounded-lg border-2 border-dashed p-6 text-center transition-colors',
    isUnaccepted ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-gray-400',
    !isUnaccepted && isDropTarget && 'border-indigo-500 bg-indigo-50'
  )

  const showError = isUnaccepted
  const showChildren = !isUnaccepted && !!children
  const showDefaultText = !isUnaccepted && !children

  return (
    <div
      {...dropProps}
      ref={containerRef}
      className={cn}
      data-testid='file-upload'
      data-drop-target={isDropTarget || undefined}
      aria-invalid={isUnaccepted}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={handleChange}
        aria-hidden="true"
        className='hidden'
      />

      <div role="button" tabIndex={0} onClick={triggerFileSelect} onKeyDown={onKeyDown} aria-label={!children ? t('common.filepicker.prompt') : undefined}>
        {showDefaultText && <p className='text-sm text-gray-500'>{t('common.filepicker.prompt')}</p>}
        {showChildren && children}
        {showError && <p className='text-sm text-red-600' role='alert'>{t('common.filepicker.unsupported')}</p>}
      </div>
    </div>
  )
}

export default FileUpload